import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { settlementsService } from '@/shared/services/settlementsService';
import { listRestaurants, Restaurant } from '@/shared/services/restaurantsService';
import { couriersService } from '@/shared/services/couriersService';
import { SettlementBatch, SettlementBatchType, SettlementBatchStatus } from '@/shared/types/settlements';
import styles from './SettlementsPage.module.css';

export const SettlementsPage = () => {
    const navigate = useNavigate();
    const [batches, setBatches] = useState<SettlementBatch[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState<SettlementBatchType | ''>('');
    const [filterStatus, setFilterStatus] = useState<SettlementBatchStatus | ''>('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [isCreatingBatch, setIsCreatingBatch] = useState(false);

    // Estado de paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [totalCount, setTotalCount] = useState(0);

    // Lista de posibles beneficiarios para el select
    const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
    const [couriers, setCouriers] = useState<any[]>([]);

    const [newBatchParams, setNewBatchParams] = useState({
        type: 'RESTAURANT' as SettlementBatchType,
        fromDate: '',
        toDate: '',
        beneficiaryId: '' // ID del restaurante o repartidor específico
    });

    // Estado para notificaciones estéticas
    const [notification, setNotification] = useState<{
        show: boolean;
        message: string;
        type: 'success' | 'error' | 'info';
        title: string;
    }>({
        show: false,
        message: '',
        type: 'info',
        title: ''
    });

    useEffect(() => {
        setCurrentPage(1); // Resetear a página 1 cuando cambian los filtros
        fetchBatches(false); // Por defecto carga sin filtros de fecha
        loadBeneficiaries();
    }, [filterType, filterStatus]);

    useEffect(() => {
        fetchBatches(!!(dateFrom && dateTo)); // Recargar cuando cambia la página
    }, [currentPage, itemsPerPage]);

    const loadBeneficiaries = async () => {
        try {
            const resData = await listRestaurants();
            setRestaurants(resData);

            // Para repartidores, podríamos usar couriersService.listAll() si está disponible
            try {
                const courierData = await couriersService.listAll({ limit: 100 });
                // @ts-ignore
                setCouriers(courierData.couriers || []);
            } catch (e) {
                console.warn('Error loading couriers for list', e);
            }
        } catch (error) {
            console.error('Error loading beneficiaries:', error);
        }
    };

    const fetchBatches = async (applyDateFilters = true) => {
        setLoading(true);
        try {
            const offset = (currentPage - 1) * itemsPerPage;
            const response = await settlementsService.listBatches({
                type: filterType || undefined,
                status: filterStatus || undefined,
                dateFrom: (applyDateFilters && dateFrom) ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
                dateTo: (applyDateFilters && dateTo) ? new Date(`${dateTo}T23:59:59`).toISOString() : undefined,
                limit: itemsPerPage,
                offset: offset
            });
            setBatches(response.batches);
            setTotalCount(response.count || 0);
        } catch (error) {
            console.error('Error fetching batches:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilter = () => {
        setCurrentPage(1); // Resetear a página 1 al filtrar
        fetchBatches(true);
    };

    const handleClearDates = () => {
        setDateFrom('');
        setDateTo('');
        fetchBatches(false); // Recargar sin los filtros de fecha
    };

    const handleCreateBatch = async () => {
        if (!newBatchParams.fromDate || !newBatchParams.toDate) {
            setNotification({
                show: true,
                title: 'Faltan Datos',
                message: 'Por favor selecciona ambas fechas.',
                type: 'info'
            });
            return;
        }

        setIsCreatingBatch(true);
        try {
            // Formatear fechas usando la zona horaria LOCAL del administrador
            // Al quitar la 'Z', el navegador interpreta el día tal como lo ve el usuario (00:00 local).
            // toISOString() luego lo pasará a UTC correctamente para el servidor.
            const formattedParams = {
                ...newBatchParams,
                fromDate: newBatchParams.fromDate, // Enviamos YYYY-MM-DD directamente
                toDate: newBatchParams.toDate,     // Enviamos YYYY-MM-DD directamente
                beneficiaryId: newBatchParams.beneficiaryId || undefined
            };
            const response = await settlementsService.createBatch(formattedParams);

            if (response.batch) {
                setShowCreateModal(false);
                setNotification({
                    show: true,
                    title: '¡Éxito!',
                    message: 'Lote creado de forma exitosa.',
                    type: 'success'
                });
                fetchBatches();
            } else {
                // Caso donde success es true pero batch es null (no se encontraron pedidos)
                setNotification({
                    show: true,
                    title: 'Sin Pedidos',
                    message: response.message || 'No se encontraron pedidos elegibles en este rango.',
                    type: 'info'
                });
            }
        } catch (error: any) {
            console.error('Error creating batch:', error);
            const errorMessage = error.response?.data?.message || 'Error al crear el lote. Verifica que existan pedidos en ese rango y que no hayan sido liquidados ya.';
            setNotification({
                show: true,
                title: 'Error',
                message: errorMessage,
                type: 'error'
            });
        } finally {
            setIsCreatingBatch(false);
        }
    };

    const formatDate = (timestamp?: any) => {
        if (!timestamp) return 'N/A';

        let date: Date;

        if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp._seconds) {
            date = new Date(timestamp._seconds * 1000);
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            return 'N/A';
        }

        if (isNaN(date.getTime())) return 'Fecha inválida';

        return date.toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'UTC' // Forzamos UTC para evitar desfases locales en la visualización de fechas de liquidación
        });
    };

    const getStatusClass = (status: SettlementBatchStatus) => {
        switch (status) {
            case 'DRAFT': return styles.statusDraft;
            case 'CONFIRMED': return styles.statusConfirmed;
            case 'PAID': return styles.statusPaid;
            case 'CANCELLED': return styles.statusCancelled;
            default: return '';
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Liquidaciones</h1>
                <button className={styles.createButton} onClick={() => setShowCreateModal(true)}>
                    Nuevo Lote
                </button>
            </div>

            <div className={styles.filters}>
                <div className={styles.filterGroup}>
                    <label>Tipo</label>
                    <select value={filterType} onChange={(e) => setFilterType(e.target.value as SettlementBatchType | '')}>
                        <option value="">Todos</option>
                        <option value="RESTAURANT">Restaurantes</option>
                        <option value="COURIER">Repartidores</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label>Estado</label>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as SettlementBatchStatus | '')}>
                        <option value="">Todos</option>
                        <option value="DRAFT">Borrador</option>
                        <option value="CONFIRMED">Confirmado</option>
                        <option value="PAID">Pagado</option>
                        <option value="CANCELLED">Cancelado</option>
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label>Desde</label>
                    <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label>Hasta</label>
                    <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                    />
                </div>
                <button
                    className={styles.confirmButton}
                    style={{ padding: '0.4rem 1.2rem', fontSize: '0.9rem', marginLeft: '1rem' }}
                    onClick={handleFilter}
                >
                    Filtrar
                </button>
                {(dateFrom || dateTo) && (
                    <button
                        className={styles.cancelButton}
                        style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', marginLeft: '0.5rem' }}
                        onClick={handleClearDates}
                    >
                        Limpiar Fechas
                    </button>
                )}
            </div>

            <div className={styles.tableContainer}>
                {loading ? (
                    <div className={styles.emptyState}>Cargando lotes...</div>
                ) : batches.length === 0 ? (
                    <div className={styles.emptyState}>No se encontraron lotes de liquidación.</div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Lote ID</th>
                                <th>Tipo</th>
                                <th>Estado</th>
                                <th>Rango Fechas</th>
                                <th>Pedidos</th>
                                <th>Total</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {batches.map((batch) => (
                                <tr key={batch.batchId}>
                                    <td>{batch.batchId.substring(0, 8)}...</td>
                                    <td>{batch.type === 'RESTAURANT' ? 'Comercio' : 'Repartidor'}</td>
                                    <td>
                                        <span className={`${styles.status} ${getStatusClass(batch.status)}`}>
                                            {batch.status}
                                        </span>
                                    </td>
                                    <td>
                                        {formatDate(batch.fromDate)} - {formatDate(batch.toDate)}
                                    </td>
                                    <td>{batch.ordersCount || 0}</td>
                                    <td>${batch.amountTotal?.toFixed(2) || '0.00'}</td>
                                    <td className={styles.actions}>
                                        <button
                                            className={styles.actionButton}
                                            title="Ver Detalle"
                                            onClick={() => navigate(`/settlements/${batch.batchId}`)}
                                        >
                                            👁️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Controles de Paginación */}
            {!loading && batches.length > 0 && (
                <div className={styles.paginationControls}>
                    <button
                        className={styles.paginationButton}
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                    >
                        ← Anterior
                    </button>
                    <span className={styles.pageInfo}>
                        Página {currentPage} de {Math.ceil(totalCount / itemsPerPage) || 1}
                        {' '}({totalCount} lotes totales)
                    </span>
                    <button
                        className={styles.paginationButton}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                        disabled={currentPage >= Math.ceil(totalCount / itemsPerPage)}
                    >
                        Siguiente →
                    </button>
                </div>
            )}

            {showCreateModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h2>Nuevo Lote de Liquidación</h2>
                        <div className={styles.filterGroup} style={{ marginBottom: '1rem' }}>
                            <label>Tipo de Liquidación</label>
                            <select
                                value={newBatchParams.type}
                                onChange={(e) => setNewBatchParams({ ...newBatchParams, type: e.target.value as SettlementBatchType })}
                            >
                                <option value="RESTAURANT">Restaurantes</option>
                                <option value="COURIER">Repartidores</option>
                            </select>
                        </div>
                        <div className={styles.filterGroup} style={{ marginBottom: '1rem' }}>
                            <label>Beneficiario (Opcional)</label>
                            <select
                                value={newBatchParams.beneficiaryId}
                                onChange={(e) => setNewBatchParams({ ...newBatchParams, beneficiaryId: e.target.value })}
                            >
                                <option value="">Todos (Lote Masivo)</option>
                                {newBatchParams.type === 'RESTAURANT'
                                    ? restaurants.map(r => (
                                        <option key={r.restaurantId} value={r.restaurantId}>{r.name}</option>
                                    ))
                                    : couriers.map(c => (
                                        <option key={c.uid} value={c.uid}>
                                            {c.fullName || `${c.firstName} ${c.lastName}` || 'Sin Nombre'}
                                        </option>
                                    ))
                                }
                            </select>
                            <small style={{ color: '#666', fontSize: '0.8rem' }}>
                                {newBatchParams.type === 'RESTAURANT'
                                    ? 'Déjalo en "Todos" para procesar a todos los COMERCIOS al mismo tiempo.'
                                    : 'Déjalo en "Todos" para procesar a todos los REPARTIDORES al mismo tiempo.'}
                            </small>
                        </div>
                        <div className={styles.filterGroup} style={{ marginBottom: '1rem' }}>
                            <label>Desde</label>
                            <input
                                type="date"
                                value={newBatchParams.fromDate}
                                onChange={(e) => setNewBatchParams({ ...newBatchParams, fromDate: e.target.value })}
                            />
                        </div>
                        <div className={styles.filterGroup} style={{ marginBottom: '1rem' }}>
                            <label>Hasta</label>
                            <input
                                type="date"
                                value={newBatchParams.toDate}
                                onChange={(e) => setNewBatchParams({ ...newBatchParams, toDate: e.target.value })}
                            />
                        </div>
                        <div className={styles.modalActions}>
                            <button className={styles.cancelButton} onClick={() => setShowCreateModal(false)}>
                                Cancelar
                            </button>
                            <button
                                className={styles.confirmButton}
                                onClick={handleCreateBatch}
                                disabled={isCreatingBatch}
                            >
                                {isCreatingBatch ? 'Procesando...' : 'Crear Lote'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {notification.show && (
                <div className={styles.modalOverlay}>
                    <div className={styles.notificationModal}>
                        <span className={styles.notificationIcon}>
                            {notification.type === 'success' ? '✅' : notification.type === 'error' ? '❌' : 'ℹ️'}
                        </span>
                        <h2 className={`${styles.notificationTitle} ${notification.type === 'success' ? styles.successTitle :
                            notification.type === 'error' ? styles.errorTitle :
                                styles.infoTitle
                            }`}>
                            {notification.title}
                        </h2>
                        <p className={styles.notificationMessage}>{notification.message}</p>
                        <button
                            className={styles.closeNotificationButton}
                            onClick={() => setNotification({ ...notification, show: false })}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
